import { restAPI } from "./index.ts";
import type {
  FlowAttributeGetListRequestType,
  FlowAttributeReferenceType,
  FlowCreateOneRequestType,
  FlowGetListRequestSchema,
  FlowGetListRequestType,
  FlowGetOneRequestType,
  FlowType,
  FlowUpdateOneRequestType,
  FlowV2Type,
} from "../schemas/FlowSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import type {
  CreateOneResponseType,
  GetListResponseType,
  GetOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
  UpdateOneResponseType,
} from "../util/index.ts";
import { fetchAndCollate, getFilterObject, Resources } from "../util/index.ts";
import { SavedFiltersAdapter } from "./composable/index.ts";
import type { SavedFiltersAdapterType } from "./composable/index.ts";

export interface FlowAdapterType
  extends Pick<
    ResourceAdapterType<FlowType>,
    | "getList"
    | "exportList"
    | "getOne"
    | "createOne"
    | "updateOne"
    | "deleteOne"
  > {
  getList: (
    params: FlowGetListRequestType
  ) => Promise<GetListResponseType<FlowV2Type>>;
  exportList: (
    params: FlowGetListRequestType
  ) => Promise<ReportWithDownloadV2Type>;
  getOne: (
    params: FlowGetOneRequestType
  ) => Promise<GetOneResponseType<FlowType>>;
  createOne: (
    params: FlowCreateOneRequestType
  ) => Promise<CreateOneResponseType<FlowType>>;
  updateOne: (
    params: FlowUpdateOneRequestType
  ) => Promise<UpdateOneResponseType<FlowType>>;
  types: {
    getList: (
      params: FlowAttributeGetListRequestType
    ) => Promise<GetListResponseType<FlowAttributeReferenceType>>;
  };
  productCategories: {
    getList: (
      params: FlowAttributeGetListRequestType
    ) => Promise<GetListResponseType<FlowAttributeReferenceType>>;
  };
  productTypes: {
    getList: (
      params: FlowAttributeGetListRequestType
    ) => Promise<GetListResponseType<FlowAttributeReferenceType>>;
  };
  savedFilters: SavedFiltersAdapterType<
    typeof FlowGetListRequestSchema.shape.filters
  >;
}

export const FlowAdapter: FlowAdapterType = {
  getList: ({
    filters,
    meta,
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
  }) => {
    return fetchAndCollate(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<FlowV2Type>>({
          version: 2,
          endpoint: "/flows/search",
          query: meta?.period ? { period: meta.period } : undefined,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            group_by: groupBy,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    );
  },

  exportList: ({ filters, meta, sortBy, sortDirection }) => {
    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/flows/export",
      query: meta?.period ? { period: meta.period } : undefined,
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: getFilterObject(filters),
      },
    });
  },

  createOne: ({
    data: {
      upstream_flow_ids,
      upstream_facility_id,
      upstream_equipment_id,
      downstream_flow_ids,
      downstream_facility_id,
      downstream_equipment_id,
      ...restCreateFlowBody
    },
  }) =>
    restAPI.nodeAPI
      .POST<FlowType>({
        endpoint: "/flows",
        body: {
          ...restCreateFlowBody,
          ...(upstream_flow_ids ? { upstream_flow_ids } : {}),
          ...(upstream_facility_id ? { upstream_facility_id } : {}),
          ...(upstream_equipment_id ? { upstream_equipment_id } : {}),
          ...(downstream_flow_ids ? { downstream_flow_ids } : {}),
          ...(downstream_facility_id ? { downstream_facility_id } : {}),
          ...(downstream_equipment_id ? { downstream_equipment_id } : {}),
        },
      })
      .then((resp) => ({ data: resp })),

  getOne: ({ id, meta }) =>
    restAPI.nodeAPI
      .GET<FlowType>({
        endpoint: `/flows/${id}`,
        query: meta?.period ? { period: meta.period } : undefined,
      })
      .then((resp) => ({
        data: resp,
      })),

  updateOne: ({
    id,
    data: {
      upstream_flow_ids,
      upstream_facility_id,
      upstream_equipment_id,
      downstream_flow_ids,
      downstream_facility_id,
      downstream_equipment_id,
      ...restEditFlowBody
    },
  }) =>
    restAPI.nodeAPI
      .PUT<FlowType>({
        endpoint: `/flows/${id}`,
        body: {
          ...restEditFlowBody,
          ...(upstream_flow_ids ? { upstream_flow_ids } : {}),
          ...(upstream_facility_id ? { upstream_facility_id } : {}),
          ...(upstream_equipment_id ? { upstream_equipment_id } : {}),
          ...(downstream_flow_ids ? { downstream_flow_ids } : {}),
          ...(downstream_facility_id ? { downstream_facility_id } : {}),
          ...(downstream_equipment_id ? { downstream_equipment_id } : {}),
        },
      })
      .then((resp) => ({ data: resp })),

  deleteOne: ({ id }) => restAPI.nodeAPI.DELETE({ endpoint: `/flows/${id}` }),

  types: {
    getList: () =>
      restAPI.nodeAPI
        .GET<FlowAttributeReferenceType[]>({
          endpoint: "/flow_types",
        })
        .then((resp) => ({
          data: resp,
          total_entries: resp.length,
          total_pages: 1,
          page_number: 1,
          page_size: resp.length,
        })),
  },

  productCategories: {
    getList: () =>
      restAPI.nodeAPI
        .GET<FlowAttributeReferenceType[]>({
          endpoint: "/product_categories",
        })
        .then((resp) => ({
          data: resp,
          total_entries: resp.length,
          total_pages: 1,
          page_number: 1,
          page_size: resp.length,
        })),
  },

  productTypes: {
    getList: () =>
      restAPI.nodeAPI
        .GET<FlowAttributeReferenceType[]>({
          endpoint: "/product_types",
        })
        .then((resp) => ({
          data: resp,
          total_entries: resp.length,
          total_pages: 1,
          page_number: 1,
          page_size: resp.length,
        })),
  },

  savedFilters: SavedFiltersAdapter({
    endpoint: "/flows/filters",
  }),
};

export const FlowAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<FlowAdapterType> =
  {
    getList: (params) => [Resources.FLOW, "getList", params],
    exportList: (params) => [Resources.FLOW, "exportList", params],
    getOne: (params) => [Resources.FLOW, "getOne", params.id],
    createOne: () => [Resources.FLOW],
    updateOne: () => [Resources.FLOW],
    deleteOne: () => [Resources.FLOW],
    types: {
      getList: () => [Resources.FLOW, "types", "getList"],
    },
    productTypes: {
      getList: () => [Resources.FLOW, "product_types", "getList"],
    },
    productCategories: {
      getList: () => [Resources.FLOW, "product_categories", "getList"],
    },
    savedFilters: {
      getList: (params) =>
        [Resources.FLOW, "filters", "getList", params] as const,
      updateOne: () => [Resources.FLOW, "filters"] as const,
      createOne: () => [Resources.FLOW, "filters"] as const,
      deleteOne: () => [Resources.FLOW, "filters"] as const,
    },
  };
